import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import './css/ZIndex.css';
import Config from './config/Config';
import {
    LoginPageWrapper,
    ContactPageWrapper,
    TermsPageWrapper,
    HowItWorksPageWrapper,
    PrivacyPolicyPageWrapper,
    CookiePolicyPageWrapper,
    DataProtectionPageWrapper,
    SavedValuationsPageWrapper,
    ValuationPageWrapper,
    ValuationPageWithoutSlugWrapper,
    AreaSearchPageWrapper,
    AreaAnalysisPageWrapper,
    PremiumPageWrapper,
    OldUrlRedirectWrapper
} from './components/navigation/PageWrappers';
import NavigationProvider from './components/navigation/NavigationProvider';
import HomePageWithRedirect from './components/navigation/HomePageWithRedirect';
import DesignSystemPage from './components/design/DesignSystemPage';
import { AppProvider } from './context/AppContext';
import type { AppDependencies } from './context/AppContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { applyUserEntitlements } from './utils/LocalDevAccess';
import ConsentProvider from './components/consent/ConsentProvider';
import { configureAnalytics, syncAnalyticsWithConsent } from './consent/analytics';
import { readStoredConsent } from './consent/consentStorage';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Instantiate app dependencies once at the root
const config = new Config();

configureAnalytics({
  posthogToken: config.deploymentEnvironmentConfig.get("POSTHOG_TOKEN"),
  posthogHost: config.deploymentEnvironmentConfig.get("POSTHOG_HOST"),
  googleAnalyticsId: "G-RT8Q4VQYL1",
  googleAdsId: "AW-17859474557",
});

const storedConsent = readStoredConsent();
syncAnalyticsWithConsent(storedConsent);

const dependencies: AppDependencies = {
    config,
    pathRouter: config.getPathRouter(),
    apiHandler: config.getApiHandler(),
    errorHandler: config.getErrorHandler(),
    loader: config.getLoader(),
    loginHelper: config.getLoginHelper(),
    stateHandler: config.getEventAdaptingStateHandler(),
    featureProvider: config.getFeatureProvider(),
    areaAnalysisBuilderChain: config.getAreaAnalysisBuilderChain(),
};
const isNonProdEnv = process.env.REACT_APP_DEPLOYMENT_ENV !== "PROD";

(async () => {
    try {
        const isLoggedIn = await dependencies.loginHelper.isLoggedIn();
        dependencies.stateHandler.set("isLoggedIn", isLoggedIn);
        if (isLoggedIn) {
            const userInfo = await dependencies.apiHandler.getUserInfo();
            const features: string[] = userInfo?.features || [];
            applyUserEntitlements(
                dependencies.stateHandler,
                features,
                userInfo?.at || "basic",
                userInfo?.free_detailed_valuation_credits_remaining ?? 0,
                userInfo?.free_unlocked_property_ids ?? []
            );
        } else {
            applyUserEntitlements(dependencies.stateHandler, [], "basic", 0, []);
        }
    } catch {
        applyUserEntitlements(dependencies.stateHandler, [], "basic", 0, []);
    }
})();

root.render(
  <React.StrictMode>
    <ConsentProvider>
    <GoogleOAuthProvider clientId="316780219697-27hs7eov4skk7hvmlmeunkvtq8m2b1p6.apps.googleusercontent.com">
    <BrowserRouter>
      <AppProvider dependencies={dependencies}>
        <NavigationProvider>
          <Routes>
        {/* Home/Search Page - handles old URL patterns */}
        <Route path="/" element={<HomePageWithRedirect />} />
        
        {/* Login Page */}
        <Route path="/login" element={<LoginPageWrapper />} />
        
        {/* Contact Page */}
        <Route path="/contact" element={<ContactPageWrapper />} />

        {/* Privacy / Cookie / Data Protection Pages */}
        <Route path="/privacy" element={<PrivacyPolicyPageWrapper />} />
        <Route path="/cookies" element={<CookiePolicyPageWrapper />} />
        <Route path="/data-protection" element={<DataProtectionPageWrapper />} />
        
        {/* Terms Page */}
        <Route path="/terms" element={<TermsPageWrapper />} />

        {/* How It Works Page */}
        <Route path="/how-it-works" element={<HowItWorksPageWrapper />} />
        
        {/* Plans Page */}
        <Route path="/plans" element={<PremiumPageWrapper />} />

        {/* Design system reference (kitchen sink) — dev/preprod only, not PROD */}
        {isNonProdEnv && <Route path="/design" element={<DesignSystemPage />} />}

        {/* Saved Valuations Page */}
        <Route path="/saved-valuations" element={<SavedValuationsPageWrapper />} />

        {/* Area Search Page */}
        <Route path="/areas" element={<AreaSearchPageWrapper />} />

        {/* Area Analysis Pages - with and without slug */}
        <Route path="/area/:areaId/:areaSlug" element={<AreaAnalysisPageWrapper />} />
        <Route path="/area/:areaId" element={<AreaAnalysisPageWrapper />} />
        
        {/* Valuation Pages - with address slug */}
        <Route path="/valuate/:propertyId/:addressSlug" element={<ValuationPageWrapper />} />

        {/* Valuation Page - without address slug (fallback) */}
        <Route path="/valuate/:propertyId" element={<ValuationPageWithoutSlugWrapper />} />
        
        {/* Catch-all: handle old URL patterns with query params */}
        <Route path="*" element={<OldUrlRedirectWrapper />} />
          </Routes>
        </NavigationProvider>
      </AppProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
    </ConsentProvider>
  </React.StrictMode>
);
